/************************************************
 * src/components/RedmineLicenseRequests.js
 ************************************************/
import React, { useState, useMemo } from 'react';
import axios from './axiosInstance';
import DataTable from 'react-data-table-component';
import Modal from 'react-modal';
import PropTypes from 'prop-types';
import './styles/RedmineLicenseRequests.css';

/************************************************
 * We only show "AIMS License Requests" here,
 * because your aggregator is storing only AIMS
 * (tracker=20) in licenseRequestsData. Then the
 * user can filter "open" vs "all" and do a search.
 ************************************************/
const REDMINE_STATUSES = [
  { id: 1, name: 'New' },
  { id: 2, name: 'In Progress' },
  { id: 3, name: 'Resolved' },
  { id: 4, name: 'Feedback' },
  { id: 5, name: 'Closed' },
];

const modalStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    backgroundColor: '#2a3b50',
    color: 'white',
    padding: '20px',
    borderRadius: '10px',
    zIndex: 1001,
    maxWidth: '700px',
    width: '90%',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    zIndex: 1000,
  },
};

const spinnerStyles = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: '50px',
  height: '50px',
  borderRadius: '50%',
  border: '6px solid rgba(0, 0, 0, 0.1)',
  borderTopColor: '#3498db',
  animation: 'spin 1s linear infinite',
};

function isPreviewable(contentType = '') {
  const lower = contentType.toLowerCase();
  if (lower.includes('pdf')) return true;
  if (
    lower.includes('jpeg') ||
    lower.includes('jpg') ||
    lower.includes('png') ||
    lower.includes('bmp') ||
    lower.includes('gif')
  ) {
    return true;
  }
  return false;
}

const RedmineLicenseRequests = ({
  licenseRequestsData, // already AIMS-only
  filterOption,
  setFilterOption,
  selectedCompany,
  setSelectedCompany,
  licenseId,
  setLicenseId,
  companyTagId,
  setCompanyTagId,
  tickets,
  setTickets,
}) => {
  // local search
  const [searchQuery, setSearchQuery] = useState('');

  // error/spinner for ticket updates & invoice upload
  const [errorMessage, setErrorMessage] = useState('');
  const [isFetching, setIsFetching] = useState(false);

  // spinner for attachment loading
  const [isAttachmentLoading, setIsAttachmentLoading] = useState(false);

  // Ticket details modal
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState(null);

  // local editing fields
  const [editSubject, setEditSubject] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStatusId, setEditStatusId] = useState(1);
  const [editNotes, setEditNotes] = useState('');

  // attachments list for selected ticket
  const [ticketAttachments, setTicketAttachments] = useState([]);

  // preview modal
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewBlobUrl, setPreviewBlobUrl] = useState(null);
  const [previewAttachment, setPreviewAttachment] = useState(null);

  // invoice upload
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState(null);

  // ============ 1) handleTicketClick =============
  const handleTicketClick = (ticket) => {
    setErrorMessage('');
    if (!ticket || !ticket['Ticket #']) {
      setErrorMessage('Invalid ticket data');
      return;
    }
    setSelectedTicketId(ticket['Ticket #']);
    setShowTicketModal(true);

    // populate subject, description, status
    const subj = ticket['Subject'] || '';
    const desc = ticket['Description'] || '';
    const stName = ticket['Status'] || 'New';
    const found = REDMINE_STATUSES.find(
      (s) => s.name.toLowerCase() === stName.toLowerCase()
    );
    setEditSubject(subj);
    setEditDescription(desc);
    setEditStatusId(found ? found.id : 1);
    setEditNotes('');

    // attachments + invoices
    const normal = ticket['Attachment'] || [];
    const invoices = ticket['Invoice'] || [];
    setTicketAttachments([...normal, ...invoices]);
  };

  // ============ 2) updateTicket ================
  const updateTicket = async () => {
    setIsFetching(true);
    try {
      const updateData = {
        issue: {
          subject: editSubject,
          description: editDescription,
          notes: editNotes,
          status_id: editStatusId,
        },
      };
      await axios.put(
        `/redmine/issues/${selectedTicketId}.json`,
        updateData
      );
      alert('Ticket updated successfully.');
    } catch (err) {
      console.error('Error updating ticket:', err);
      alert('Failed to update ticket.');
    } finally {
      setIsFetching(false);
    }
  };

  // ======== 3) handleAttachmentClick =============
  const handleAttachmentClick = async (attachment) => {
    if (!attachment) return;
    const url = `/redmine/attachments/download/${attachment.id}`;

    // open modal & show spinner immediately
    setShowPreviewModal(true);
    setPreviewAttachment(null);
    setPreviewBlobUrl(null);
    setIsAttachmentLoading(true);

    // direct download if not previewable
    if (!isPreviewable(attachment.content_type)) {
      try {
        const { data: blob } = await axios.get(url, {
          responseType: 'blob',
        });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = attachment.filename;
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch (err) {
        console.error('Download error:', err);
        alert('Failed to download file.');
      } finally {
        setIsAttachmentLoading(false);
      }
      return;
    }

    // preview PDF/image
    try {
      const { data: blob } = await axios.get(url, {
        responseType: 'blob',
      });
      const objectUrl = URL.createObjectURL(blob);
      setPreviewBlobUrl(objectUrl);
      setPreviewAttachment(attachment);
    } catch (err) {
      console.error('Preview error:', err);
      alert('Cannot preview file or route missing.');
      // leave modal open so user can close
    } finally {
      setIsAttachmentLoading(false);
    }
  };

  // ======== directDownload helper ================
  const directDownload = async (id, filename) => {
    setIsAttachmentLoading(true);
    try {
      const { data: blob } = await axios.get(
        `/redmine/attachments/download/${id}`,
        { responseType: 'blob' }
      );
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Download error:', err);
      alert('Failed to download file.');
    } finally {
      setIsAttachmentLoading(false);
    }
  };

  // ======== 4) handleApprovalChange =============
  const handleApprovalChange = async (ticketId, newVal) => {
    try {
      await axios.put(`/redmine/issues/${ticketId}.json`, {
        issue: {
          custom_fields: [{ id: 66, value: newVal }],
        },
      });
      setTickets(
        tickets.map((t) =>
          t['Ticket #'] === ticketId ? { ...t, Approval: newVal } : t
        )
      );
    } catch (err) {
      console.error('Error updating approval:', err);
      alert('Failed to update approval field.');
    }
  };

  // ======== 5) Invoice upload ================
  const openInvoiceModal = (ticket) => {
    setSelectedTicketId(ticket['Ticket #']);
    setShowInvoiceModal(true);
    setInvoiceFile(null);
  };
  const handleInvoiceFileChange = (e) =>
    setInvoiceFile(e.target.files?.[0] || null);
  const handleConfirmInvoiceUpload = async () => {
    if (!invoiceFile) {
      alert('Missing file');
      return;
    }
    setIsFetching(true);
    try {
      const formData = new FormData();
      formData.append('file', invoiceFile);
      await axios.post(
        `/redmine/${selectedTicketId}/upload-invoice`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      alert('Invoice uploaded successfully.');
    } catch (err) {
      console.error('Error uploading invoice:', err);
      alert('Failed to upload invoice.');
    } finally {
      setIsFetching(false);
      setShowInvoiceModal(false);
    }
  };

  // ======== 6) Build the columns =============
  const columns = [
    {
      name: 'Ticket #',
      cell: (row) => (
        <a
          href={row['Redmine Link']}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: '#00ffdd', textDecoration: 'underline' }}
        >
          #{row['Ticket #']}
        </a>
      ),
      width: '7%',
      sortable: true,
    },
    {
      name: 'Company',
      selector: (row) => row['Company'] || 'N/A',
      width: '10%',
      sortable: true,
    },
    {
      name: 'Subject',
      selector: (row) => row['Subject'] || 'No Subject',
      width: '20%',
    },
    {
      name: 'Description',
      cell: (row) => (
        <div style={{ maxHeight: '7em', overflowY: 'auto', whiteSpace: 'normal' }}>
          {row['Description']}
        </div>
      ),
      width: '20%',
    },
    {
      name: 'Status',
      selector: (row) => row['Status'] || 'N/A',
      width: '8%',
      sortable: true,
    },
    {
      name: 'Approval',
      cell: (row) => (
        <select
          value={row['Approval'] || 'None'}
          onChange={(e) => handleApprovalChange(row['Ticket #'], e.target.value)}
        >
          <option value="Approved">Approved</option>
          <option value="Not Approved">Not Approved</option>
          <option value="None">None</option>
        </select>
      ),
      width: '7%',
    },
    {
      name: 'Invoice',
      cell: (row) => {
        const invoiceFiles = row['Invoice'] || [];
        if (invoiceFiles.length === 0) {
          return (
            <div>
              <span>No invoice</span>
              <button onClick={() => openInvoiceModal(row)} style={{ marginLeft: 8 }}>
                Upload
              </button>
            </div>
          );
        }
        return invoiceFiles.map((inv) => (
          <button
            key={inv.id}
            onClick={() => handleAttachmentClick(inv)}
            style={{
              background: 'none',
              border: 'none',
              color: '#3498db',
              textDecoration: 'underline',
              cursor: 'pointer',
              marginRight: 8,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: 140,
            }}
          >
            {inv.filename}
          </button>
        ));
      },
      width: '10%',
    },
    {
      name: 'Attachment',
      cell: (row) => {
        const attachments = row['Attachment'] || [];
        if (attachments.length === 0) return 'No attachment';
        if (attachments.length === 1) {
          const one = attachments[0];
          return (
            <button
              onClick={() => handleAttachmentClick(one)}
              style={{
                background: 'none',
                border: 'none',
                color: '#3498db',
                cursor: 'pointer',
                textDecoration: 'underline',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 140,
              }}
            >
              {one.filename}
            </button>
          );
        }
        return `${attachments.length} files`;
      },
      width: '10%',
    },
  ];

  // ======== 7) final filter + search ===========
  const finalData = useMemo(() => {
    let data = licenseRequestsData || [];
    if (filterOption === 'open') {
      data = data.filter((t) => {
        const st = (t['Status'] || '').toLowerCase();
        return st !== 'closed' && st !== 'resolved';
      });
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      data = data.filter((row) =>
        Object.values(row).some(
          (val) => val?.toString().toLowerCase().includes(q)
        )
      );
    }
    return data;
  }, [licenseRequestsData, filterOption, searchQuery]);

  return (
    <div className="licenseRequestDiv">
      {/* Search & Filter */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search..."
          style={{ padding: '3px 6px', borderRadius: 3, fontSize: '12px' }}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <label style={{ color: 'white', fontSize: '10px', margin: 0 }}>Filter:</label>
          <select
            value={filterOption}
            onChange={(e) => setFilterOption(e.target.value)}
            style={{ padding: '3px 6px', borderRadius: 3, fontSize: '12px' }}
          >
            <option value="all">All License Requests</option>
            <option value="open">Open License Requests</option>
          </select>
        </div>
      </div>

      {/* DataTable (never unmounts on attachment load) */}
      <DataTable
        columns={columns}
        data={finalData}
        pagination
        highlightOnHover
        pointerOnHover
        onRowClicked={handleTicketClick}
        customStyles={{
          table: {
            style: {
              width: '100%',
              minWidth: 1000,
              overflowX: 'auto',
            },
          },
          headRow: {
            style: {
              backgroundColor: '#2a3b50',
              color: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 1,
            },
          },
          rows: {
            style: {
              minHeight: 36,
              fontSize: '11px',
              '&:not(:last-of-type)': {
                borderBottom: '1px solid #ddd',
              },
            },
            highlightOnHoverStyle: {
              backgroundColor: 'rgba(0, 17, 255, 0.466)',
              boxShadow: '0 4px 10px rgba(0, 0, 0, 0.4)',
            },
          },
          cells: {
            style: {
              padding: '4px 6px',
              fontSize: '11px',
            },
          },
          headCells: {
            style: {
              padding: '6px 6px',
              fontSize: '11px',
              fontWeight: 'bold',
            },
          },
        }}
      />

      {errorMessage && <p style={{ color: 'red', marginTop: 10 }}>{errorMessage}</p>}

      {/* TICKET DETAILS MODAL */}
      <Modal
        isOpen={showTicketModal}
        onRequestClose={() => setShowTicketModal(false)}
        style={modalStyles}
      >
        {isFetching ? (
          <div style={spinnerStyles} />
        ) : (
          <div>
            <h2 style={{ color: 'white' }}>Ticket Details</h2>
            <p>
              <strong>Ticket #:</strong> {selectedTicketId}
            </p>
            {errorMessage && <p style={{ color: 'red' }}>{errorMessage}</p>}

            {/* Subject */}
            <div style={{ marginTop: 10 }}>
              <label style={{ color: 'white' }}>Subject:</label>
              <input
                type="text"
                style={{ width: '100%', marginBottom: 8 }}
                value={editSubject}
                onChange={(e) => setEditSubject(e.target.value)}
              />
            </div>

            {/* Description */}
            <div style={{ marginTop: 10 }}>
              <label style={{ color: 'white' }}>Description:</label>
              <textarea
                style={{ width: '100%', height: 80 }}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
              />
            </div>

            {/* Status */}
            <div style={{ marginTop: 10 }}>
              <label style={{ color: 'white' }}>Status:</label>
              <select
                style={{ width: '100%', marginBottom: 8 }}
                value={editStatusId}
                onChange={(e) => setEditStatusId(Number(e.target.value))}
              >
                {REDMINE_STATUSES.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes */}
            <div style={{ marginTop: 10 }}>
              <label style={{ color: 'white' }}>Notes:</label>
              <textarea
                style={{ width: '100%', height: 80 }}
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
              />
            </div>

            {/* Attachments */}
            <h3 style={{ marginTop: 15 }}>Attachments:</h3>
            {ticketAttachments.length > 0 ? (
              <ul>
                {ticketAttachments.map((att, idx) => (
                  <li key={idx}>
                    <button
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#3498db',
                        textDecoration: 'underline',
                        cursor: 'pointer',
                        padding: 0,
                      }}
                      onClick={() => handleAttachmentClick(att)}
                    >
                      {att.filename} ({(att.filesize / 1024 / 1024).toFixed(2)} MB)
                    </button>
                  </li>
                ))}
              </ul>
            ) : (
              <p>No attachments available.</p>
            )}

            {/* Actions */}
            <div style={{ marginTop: 20, textAlign: 'right' }}>
              <button
                onClick={updateTicket}
                style={{
                  marginRight: 10,
                  backgroundColor: '#4caf50',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 15px',
                  borderRadius: 5,
                  cursor: 'pointer',
                }}
              >
                Update Ticket
              </button>
              <button
                onClick={() => setShowTicketModal(false)}
                style={{
                  backgroundColor: '#555',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 15px',
                  borderRadius: 5,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* ATTACHMENT PREVIEW MODAL */}
      <Modal
        isOpen={showPreviewModal}
        onRequestClose={() => {
          if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
          setShowPreviewModal(false);
          setPreviewBlobUrl(null);
          setPreviewAttachment(null);
        }}
        style={modalStyles}
      >
        <h2 style={{ color: 'white' }}>Attachment Preview</h2>
        {isAttachmentLoading ? (
          <div style={spinnerStyles} />
        ) : previewAttachment && previewBlobUrl ? (
          <>
            {previewAttachment.content_type.toLowerCase().includes('pdf') ? (
              <iframe
                src={previewBlobUrl}
                style={{ width: '100%', height: 500, backgroundColor: '#fff' }}
                title="PDF Preview"
              />
            ) : (
              <img
                src={previewBlobUrl}
                alt={previewAttachment.filename}
                style={{ maxWidth: '100%', maxHeight: 500 }}
              />
            )}
            <div style={{ marginTop: 15, textAlign: 'right' }}>
              <button
                onClick={() => directDownload(previewAttachment.id, previewAttachment.filename)}
                style={{
                  backgroundColor: '#4caf50',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 15px',
                  borderRadius: 5,
                  cursor: 'pointer',
                  marginRight: 10,
                }}
              >
                Download
              </button>
              <button
                onClick={() => {
                  if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
                  setShowPreviewModal(false);
                  setPreviewBlobUrl(null);
                  setPreviewAttachment(null);
                }}
                style={{
                  backgroundColor: '#555',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 15px',
                  borderRadius: 5,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <p>No attachment selected or no preview available.</p>
        )}
      </Modal>

      {/* INVOICE UPLOAD MODAL */}
      <Modal
        isOpen={showInvoiceModal}
        onRequestClose={() => setShowInvoiceModal(false)}
        style={modalStyles}
      >
        <h2 style={{ color: 'white' }}>Upload Invoice</h2>
        {isFetching ? (
          <div style={spinnerStyles} />
        ) : (
          <div>
            <p>Select a file to attach with “RM-INVC ” prefix in Redmine.</p>
            <input type="file" onChange={handleInvoiceFileChange} />
            <div style={{ marginTop: 20, textAlign: 'right' }}>
              <button
                onClick={handleConfirmInvoiceUpload}
                style={{
                  marginRight: 10,
                  backgroundColor: '#4caf50',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 15px',
                  borderRadius: 5,
                  cursor: 'pointer',
                }}
              >
                Upload
              </button>
              <button
                onClick={() => setShowInvoiceModal(false)}
                style={{
                  backgroundColor: '#555',
                  color: '#fff',
                  border: 'none',
                  padding: '10px 15px',
                  borderRadius: 5,
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

RedmineLicenseRequests.propTypes = {
  licenseRequestsData: PropTypes.array.isRequired,
  filterOption: PropTypes.string.isRequired,
  setFilterOption: PropTypes.func.isRequired,
  selectedCompany: PropTypes.object.isRequired,
  setSelectedCompany: PropTypes.func.isRequired,
  licenseId: PropTypes.string.isRequired,
  setLicenseId: PropTypes.func.isRequired,
  companyTagId: PropTypes.string.isRequired,
  setCompanyTagId: PropTypes.func.isRequired,
  tickets: PropTypes.array.isRequired,
  setTickets: PropTypes.func.isRequired,
};

export default RedmineLicenseRequests;
